import { z } from 'zod';
import { TIPOS_ORDEN } from '../dominio.js';
import { estado, opcional, uuid } from './comunes.js';
/**
 * LOG-FT-02 — Orden de Compra / Servicio.
 *
 * Compra y servicio comparten formato y tabla, distinguidos por `tipo`, pero
 * llevan correlativos separados: ambos pueden ser LOG-FT-02-004.
 */
export const AFECTACIONES = ['GRAVADO', 'EXONERADO', 'INAFECTO'];
export const ordenItemEntrada = z.object({
    itemNumero: z.number().int().min(1).max(999),
    insumoId: opcional(z.number().int().positive()),
    descripcion: z.string().trim().min(3, 'Describa el ítem').max(2000),
    unidadId: z.number().int().positive('Elija una unidad'),
    cantidad: z.number().positive('La cantidad debe ser mayor a cero').max(99_999_999),
    precioUnitario: z.number().min(0, 'El precio no puede ser negativo').max(99_999_999),
});
export const ordenEntrada = z
    .object({
    empresaId: z.number().int().positive('Elija la empresa emisora'),
    correlativo: z.number().int().min(1, 'El correlativo debe ser mayor a cero').max(999_999),
    tipo: z.enum(TIPOS_ORDEN),
    proveedorId: uuid,
    requerimientoId: opcional(uuid),
    fechaEmision: z.iso.date(),
    obraId: opcional(z.number().int().positive()),
    areaId: opcional(z.number().int().positive()),
    monedaId: z.number().int().positive('Elija la moneda'),
    tipoCambio: opcional(z.number().positive('El tipo de cambio debe ser mayor a cero')),
    afectacion: z.enum(AFECTACIONES).default('GRAVADO'),
    igvPorcentaje: z.number().min(0).max(0.9999).default(0.18),
    formaPagoId: opcional(z.number().int().positive()),
    tiempoEntrega: opcional(z.string().trim().max(200)),
    lugarEntrega: opcional(z.string().trim().max(300)),
    sujetaDetraccion: z.boolean().default(false),
    // Retención de renta de cuarta categoría: el proveedor persona natural
    // factura el total, pero se le transfiere menos porque ese 8% va a SUNAT.
    // No altera el total; solo el neto a pagar, que deriva la base.
    sujetaRetencion: z.boolean().default(false),
    retencionPorcentaje: z.number().min(0).max(0.9999).default(0.08),
    totalEnLetras: opcional(z.string().trim().max(300)),
    observaciones: opcional(z.string().trim().max(2000)),
    items: z.array(ordenItemEntrada).min(1, 'Agregue al menos un ítem').max(200),
})
    .refine((v) => v.afectacion === 'GRAVADO' || v.igvPorcentaje === 0, {
    path: ['igvPorcentaje'],
    message: 'Una orden exonerada o inafecta no lleva IGV',
});
export const ordenEdicion = z.object({
    empresaId: z.number().int().positive().optional(),
    correlativo: z.number().int().min(1).max(999_999).optional(),
    proveedorId: uuid.optional(),
    requerimientoId: opcional(uuid),
    fechaEmision: z.iso.date().optional(),
    obraId: opcional(z.number().int().positive()),
    areaId: opcional(z.number().int().positive()),
    monedaId: z.number().int().positive().optional(),
    tipoCambio: opcional(z.number().positive()),
    afectacion: z.enum(AFECTACIONES).optional(),
    igvPorcentaje: z.number().min(0).max(0.9999).optional(),
    formaPagoId: opcional(z.number().int().positive()),
    tiempoEntrega: opcional(z.string().trim().max(200)),
    lugarEntrega: opcional(z.string().trim().max(300)),
    sujetaDetraccion: z.boolean().optional(),
    sujetaRetencion: z.boolean().optional(),
    retencionPorcentaje: z.number().min(0).max(0.9999).optional(),
    totalEnLetras: opcional(z.string().trim().max(300)),
    observaciones: opcional(z.string().trim().max(2000)),
    items: z.array(ordenItemEntrada).min(1).max(200).optional(),
});
export const ordenItem = z.object({
    id: z.number().int(),
    itemNumero: z.number().int(),
    insumoId: z.number().int().nullable(),
    descripcion: z.string(),
    unidadId: z.number().int(),
    unidadCodigo: z.string().nullable(),
    cantidad: z.number(),
    precioUnitario: z.number(),
    /** Calculado por la base: cantidad × precio unitario. */
    parcial: z.number(),
});
export const ordenResumen = z.object({
    id: uuid,
    numero: z.string(),
    tipo: z.enum(TIPOS_ORDEN),
    estado,
    fechaEmision: z.string(),
    empresa: z.string(),
    proveedor: z.string(),
    proveedorRuc: z.string(),
    moneda: z.string(),
    total: z.number(),
    obra: z.string().nullable(),
    requerimientoNumero: z.string().nullable(),
});
export const orden = ordenResumen.extend({
    empresaId: z.number().int(),
    correlativo: z.number().int(),
    proveedorId: uuid,
    requerimientoId: uuid.nullable(),
    obraId: z.number().int().nullable(),
    areaId: z.number().int().nullable(),
    area: z.string().nullable(),
    monedaId: z.number().int(),
    tipoCambio: z.number().nullable(),
    afectacion: z.enum(AFECTACIONES),
    igvPorcentaje: z.number(),
    /** Los tres los calcula la base desde los ítems; la aplicación no los envía. */
    subtotal: z.number(),
    igvMonto: z.number(),
    formaPagoId: z.number().int().nullable(),
    tiempoEntrega: z.string().nullable(),
    lugarEntrega: z.string().nullable(),
    sujetaDetraccion: z.boolean(),
    sujetaRetencion: z.boolean(),
    retencionPorcentaje: z.number(),
    /** Derivados por la base: total × tasa, y total − retención. */
    retencionMonto: z.number(),
    netoPagar: z.number(),
    totalEnLetras: z.string().nullable(),
    observaciones: z.string().nullable(),
    items: z.array(ordenItem),
    condiciones: z.array(z.object({ ordenVisual: z.number().int(), texto: z.string() })),
});
//# sourceMappingURL=orden.js.map